import { captureException } from "@sentry/react";
import type React from "react";
import { twJoin } from "tailwind-merge";

import type { UserDto } from "@/api/types";
import { MentionComposer, type MentionComposerValue } from "@/components/CommunityFeed/MentionComposer";
import type { FormDocument } from "@/components/DocumentInput/useDocumentInput";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import type { FormVideo } from "@/components/VideoInput/useVideoInput";
import { ContentBlock } from "@/design-system/ContentBlock";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { unfurlDeepLinkFromContent } from "@/helpers/deep-link";
import {
  MAXIMUM_DOCUMENT_FILE_SIZE_IN_MEGA_BYTES,
  MAXIMUM_IMAGE_FILE_SIZE_IN_MEGA_BYTES,
  MAXIMUM_VIDEO_FILE_SIZE_IN_MEGA_BYTES,
  megaBytesToBytes,
} from "@/helpers/file-size";
import { mergeRefs } from "@/helpers/mergeRefs";
import { twResolve } from "@/helpers/tw-resolve";
import { useBool } from "@/hooks/useBool";
import { useShouldSubmitOnEnter } from "@/hooks/useShouldSubmitOnEnter";
import { CommunityContentRail } from "@/product-patterns/community/CommunityContentRail";
import { Trans, useTranslation } from "@/translations";
import { useInputTooltipFloating } from "@/ui-adapters/useInputTooltipFloating";

import { IconButton } from "../Button/IconButton";
import { EntityPreview } from "../EntityPreview/EntityPreview";
import { XCloseIcon } from "../Icons/XCloseIcon";
import { InputTooltip } from "../InputTooltip/InputTooltip";
import { UserAvatar } from "../UserAvatar/UserAvatar";
import { CommentFieldActions } from "./components/CommentFieldActions";
import { CommentFieldDocumentPreview } from "./components/CommentFieldDocumentPreview";
import { CommentFieldGallery } from "./components/CommentFieldGallery";
import { CommentFieldInput } from "./components/CommentFieldInput";
import { CommentFieldVideoPreview } from "./components/CommentFieldVideoPreview";

/**
 * Enables structured mentions when all three properties are provided.
 * Callers keep `value` synchronized with `mentionValue.content` in `onMentionChange` because the field uses both.
 */
type CommentFieldMentionProps =
  | {
      mentionValue: MentionComposerValue;
      onMentionChange: (value: MentionComposerValue) => void;
      messageId: string;
    }
  | {
      mentionValue?: never;
      onMentionChange?: never;
      messageId?: never;
    };

export type CommentFieldProps = {
  value: string | undefined;
  tooltips?: {
    root?: (params: { onClose: () => void }) => React.ReactNode;
    send?: string;
    addImage?: string;
    addVideo?: string;
    addDocument?: string;
  };
  isEdit?: boolean;
  disabled?: boolean;
  allowedAttachments?: ("image" | "video" | "document")[];
  images?: FormImage[];
  videos?: FormVideo[];
  documents?: FormDocument[];
  onSubmit: () => Promise<void>;
  onChange: (value: string) => void;
  onAddImages?: (images: FileList) => void;
  onRemoveImage?: (image: FormImage) => void;
  onAddVideo?: (video: FileList) => void;
  onRemoveVideo?: (video: FormVideo) => void;
  onAddDocument?: (document: FileList) => void;
  onRemoveDocument?: (document: FormDocument) => void;
  onCancel?: () => void;
  disableSubmitOnEnter?: boolean;
  submissionMode?: "blocking" | "optimistic";
  actionComponent?: React.ReactNode;
  ref?: React.Ref<HTMLTextAreaElement>;
  user: UserDto;
  placeholder?: string;
} & CommentFieldMentionProps &
  Pick<React.InputHTMLAttributes<HTMLInputElement>, "autoFocus">;

export function CommentField({
  ref,
  value,
  images,
  videos,
  documents,
  disableSubmitOnEnter,
  actionComponent,
  ...props
}: CommentFieldProps): React.ReactNode {
  const [isSubmitting, setSubmitting] = useBool();
  const [isTyping, typingStateHandler] = useBool();

  const showFlashToast = useFlashToast();
  const shouldSubmitOnEnter = useShouldSubmitOnEnter();
  const { t } = useTranslation();

  async function handleSubmit() {
    if (!canSendComment || isSubmitting) {
      return;
    }

    try {
      if (props.submissionMode === "optimistic") {
        await props.onSubmit();

        return;
      }
      setSubmitting.setTrue();
      await props.onSubmit();
    } catch (error) {
      // Mutation handlers provide user feedback; retain the draft and report a handled failure.
      captureException(error);
    } finally {
      setSubmitting.setFalse();
    }
  }

  function handleAddDocument(documents: FileList) {
    const maxDocumentFileSize = megaBytesToBytes(MAXIMUM_DOCUMENT_FILE_SIZE_IN_MEGA_BYTES);
    if (validateFileSizes(documents, maxDocumentFileSize)) {
      props.onAddDocument?.(documents);
    } else {
      showFlashToast({
        message: t("components.form.error.file-too-big", {
          sizeInMegaBytes: maxDocumentFileSize,
        }),
        variant: "alert",
      });
    }
  }

  function handleAddVideo(videos: FileList) {
    const maxVideoFileSize = megaBytesToBytes(MAXIMUM_VIDEO_FILE_SIZE_IN_MEGA_BYTES);
    if (validateFileSizes(videos, maxVideoFileSize)) {
      props.onAddVideo?.(videos);
    } else {
      showFlashToast({
        message: t("components.form.error.file-too-big", {
          sizeInMegaBytes: maxVideoFileSize,
        }),
        variant: "alert",
      });
    }
  }

  function handleAddImages(images: FileList) {
    const maxImageFileSize = megaBytesToBytes(MAXIMUM_IMAGE_FILE_SIZE_IN_MEGA_BYTES);
    if (validateFileSizes(images, maxImageFileSize)) {
      props.onAddImages?.(images);
    } else {
      showFlashToast({
        message: t("components.form.error.file-too-big", {
          sizeInMegaBytes: maxImageFileSize,
        }),
        variant: "alert",
      });
    }
  }

  const tooltip = useInputTooltipFloating();
  const mentionReferenceProps = props.tooltips?.root
    ? tooltip.getReferenceProps({
        onFocus: typingStateHandler.setTrue,
        onBlur: typingStateHandler.setFalse,
      })
    : {
        onFocus: typingStateHandler.setTrue,
        onBlur: typingStateHandler.setFalse,
      };

  const { entity: attachedEntity } = unfurlDeepLinkFromContent(value || "");
  const isContentAvailable = !!value && value.trim().length > 0;
  const isImageAttachmentAvailable = !!images && images.length > 0;
  const isDocumentAttachmentAvailable = !!documents && documents.length > 0;
  const isVideoAttachmentAvailable = !!videos && videos.length > 0;
  const isAnyAttachmentAvailable =
    isImageAttachmentAvailable || isDocumentAttachmentAvailable || isVideoAttachmentAvailable;
  const isAttachedEntityPresent = Boolean(attachedEntity);
  const canSendComment = isContentAvailable || isAnyAttachmentAvailable;

  return (
    <section className="flex flex-1 flex-col gap-space-sm">
      {actionComponent && (
        <header className="flex flex-row justify-between gap-space-sm">
          <UserAvatar image={props.user.avatar} isUserDeleted={!!props.user.deletedAt} />
          {actionComponent}
        </header>
      )}
      <section className="flex w-full gap-space-sm">
        {actionComponent ? (
          <div className="hidden size-control-md shrink-0 @xs:block" aria-hidden />
        ) : (
          <UserAvatar image={props.user.avatar} isUserDeleted={!!props.user.deletedAt} />
        )}
        <section className="flex flex-1 flex-col gap-space-sm">
          <Inline gap="sm" align="start">
            <section className="inline-flex flex-1 flex-col items-center gap-space-sm @xl:min-w-xs">
              <section
                className={twResolve(
                  "flex w-full flex-1 flex-col items-center rounded-lg border border-border-secondary bg-background-default p-space-xs focus-within:border-border-brand-secondary",
                  isSubmitting && "pointer-events-none opacity-30",
                  props.disabled && "bg-background-secondary",
                )}
              >
                <InputTooltip tooltip={tooltip}>{props.tooltips?.root?.({ onClose: tooltip.close })}</InputTooltip>
                {props.mentionValue ? (
                  <MentionComposer
                    ref={mergeRefs(tooltip.referenceRef, ref)}
                    value={props.mentionValue}
                    onChange={props.onMentionChange}
                    data-testid="comment-field"
                    messageId={props.messageId}
                    placeholder={props.placeholder}
                    appearance="bare"
                    disabled={props.disabled}
                    pending={isSubmitting}
                    isEdit={props.isEdit}
                    autoFocus={props.autoFocus && shouldSubmitOnEnter}
                    submitOnEnter={shouldSubmitOnEnter && !disableSubmitOnEnter}
                    onSubmit={handleSubmit}
                    {...mentionReferenceProps}
                  />
                ) : (
                  <CommentFieldInput
                    ref={mergeRefs(tooltip.referenceRef, ref)}
                    autoFocus={props.autoFocus}
                    value={value}
                    isEdit={props.isEdit}
                    isSubmitting={isSubmitting}
                    onChange={props.onChange}
                    placeholder={props.placeholder}
                    disabled={props.disabled}
                    onSubmit={handleSubmit}
                    onFocus={typingStateHandler.setTrue}
                    onBlur={typingStateHandler.setFalse}
                    disableSubmitOnEnter={disableSubmitOnEnter}
                    {...(props.tooltips?.root ? tooltip.getReferenceProps() : {})}
                  />
                )}
                <footer
                  className={twResolve(
                    "flex h-control-md items-center self-end bg-transparent text-text-secondary",
                    isSubmitting && "pointer-events-none text-text-disabled",
                  )}
                >
                  {props.allowedAttachments?.includes("image") && (
                    <CommentFieldActions.AddImage
                      isSubmitting={isSubmitting}
                      disabled={props.disabled || (documents && documents.length > 0) || (videos && videos.length > 0)}
                      tooltip={props.tooltips?.addImage}
                      onAddImages={handleAddImages}
                    />
                  )}
                  {props.allowedAttachments?.includes("video") && (
                    <CommentFieldActions.AddVideo
                      isSubmitting={isSubmitting}
                      disabled={props.disabled || (documents && documents.length > 0) || (images && images.length > 0)}
                      tooltip={props.tooltips?.addVideo}
                      onAddVideo={handleAddVideo}
                    />
                  )}
                  {props.allowedAttachments?.includes("document") && (
                    <CommentFieldActions.AddDocument
                      isSubmitting={isSubmitting}
                      disabled={props.disabled || (images && images.length > 0) || (videos && videos.length > 0)}
                      tooltip={props.tooltips?.addDocument}
                      onAddDocument={handleAddDocument}
                    />
                  )}
                  <Inline gap="sm" align="center" wrap={false}>
                    <CommentFieldActions.Send
                      isSubmitting={isSubmitting}
                      isEdit={props.isEdit}
                      disabled={!canSendComment}
                      tooltip={props.tooltips?.send}
                      onSend={handleSubmit}
                    />
                    {props.onCancel && (
                      <IconButton
                        onClick={props.onCancel}
                        size="sm"
                        icon={<XCloseIcon />}
                        variant="soft"
                        title={t("common.action.cancel")}
                      />
                    )}
                  </Inline>
                </footer>
              </section>
              {shouldSubmitOnEnter && (
                <aside
                  className={twJoin(
                    "flex w-full justify-end transition-opacity duration-75",
                    isTyping ? "opacity-100" : "opacity-0",
                  )}
                >
                  <span className="text-overline text-text-tertiary">
                    <Trans
                      i18nKey="component.comment-field.helper-text.enter-key-behaviour"
                      values={{ keys: disableSubmitOnEnter ? "Return" : "Shift + Return" }}
                      components={{ b: <b className="text-overline-emphasis" /> }}
                    />
                  </span>
                </aside>
              )}
            </section>
          </Inline>
          {(isAnyAttachmentAvailable || isAttachedEntityPresent) && (
            <CommunityContentRail>
              <Stack gap="md">
                {isAnyAttachmentAvailable && (
                  <>
                    {images && images.length > 0 && (
                      <CommentFieldGallery images={images} onRemoveImage={props.onRemoveImage} />
                    )}
                    {videos && videos.length > 0 && (
                      <CommentFieldVideoPreview video={videos[0]} onRemoveVideo={props.onRemoveVideo} />
                    )}
                    {documents && documents.length > 0 && (
                      <CommentFieldDocumentPreview documents={documents} onRemoveDocument={props.onRemoveDocument} />
                    )}
                  </>
                )}
                {attachedEntity && (
                  <ContentBlock as="aside" width="flow" align="left" data-testid="attached-entity-preview">
                    <EntityPreview
                      entityId={attachedEntity.payload.entityId}
                      entityType={attachedEntity.payload.entityType}
                      otherIds={{
                        folderId: attachedEntity.payload.folderId,
                      }}
                      readOnly
                      shouldLoad
                    />
                  </ContentBlock>
                )}
              </Stack>
            </CommunityContentRail>
          )}
        </section>
      </section>
    </section>
  );
}

function validateFileSizes(files: FileList, maxSize: number): boolean {
  return Array.from(files).every((file) => file.size <= maxSize);
}
